import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { TokenService } from '@rappider/authentication/services';
import { catchError, Observable, of } from 'rxjs';
import { switchMap, filter, first } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import * as lodash from 'lodash';
import {
  ComponentDefinitionControllerService,
  ApproveComponentDraftRequestDtoPartial,
  ComponentDefinition
} from '@rappider/rappider-sdk';
import { selectActiveProjectId } from 'libs/project/src/lib/states/active-project-state/active-project.selectors';

export interface ComponentCompilingProcessStatus {
  id: string;
  componentDefinitionId: string;
  status: string;
  startDate: string;
  endDate: string;
}

@Injectable({ providedIn: 'root' })
export class ComponentGeneratorService {
  private generateComponentByAiPath = '/component-definitions/generate-by-ai-assistant';

  constructor(
    private http: HttpClient,
    private tokenService: TokenService,
    private componentDefinitionControllerService: ComponentDefinitionControllerService,
    private store: Store
  ) { }

  /**
   * Generate component with AI Assistant
   * @param message - Required prompt sent to AI assistant describing visual design goals, layout, style, animations, and component expectations
   * @param pageId - Optional page ID for component placement
   * @param contentTreeItemId - Optional content tree item ID for component placement
   * @param files - Optional reference files to upload
   * @param componentFramework - Optional target framework (angular|flutter). Defaults to angular
   * @param isEntityComponent - Optional flag indicating if this is an entity component
   * @param relatedProjectModelId - Optional related project model ID
   * @param model - Optional AI model ID override. Defaults to 'gemini-2.5-pro' (e.g., gpt-5-mini, gemini-2.5-pro). Defaults to 'gemini-2.5-pro'
   * @param draftId - Optional draft ID to continue from existing draft in the same chat
   * @returns Observable with response containing draftId, componentId, stepId, and summary
   */
  public generateComponent(
    message: string,
    pageId: string | undefined,
    contentTreeItemId: string | undefined,
    files?: File[] | undefined,
    componentFramework?: 'angular' | 'flutter' | undefined,
    isEntityComponent?: boolean | undefined,
    relatedProjectModelId?: string | undefined,
    model?: string | undefined,
    draftId?: string | undefined
  ): Observable<any> {
    const authenticationToken = this.tokenService.getAuthenticationToken();
    const formData = new FormData();

    formData.append('message', message);

    if (pageId) {
      formData.append('pageId', pageId);
    }

    if (contentTreeItemId) {
      formData.append('contentTreeItemId', contentTreeItemId);
    }

    if (files?.length) {
      files.forEach(file => {
        formData.append('file', file);
      });
    }

    if (isEntityComponent) {
      formData.append('isEntityComponent', 'true');
    }

    if (relatedProjectModelId) {
      formData.append('relatedProjectModelId', relatedProjectModelId);
    }

    if (componentFramework) {
      formData.append('componentFramework', componentFramework);
    } else {
      formData.append('componentFramework', 'angular');
    }

    if (draftId) {
      formData.append('draftId', draftId);
    }

    const modelToUse = model || 'gemini-2.5-pro';
    formData.append('model', modelToUse);

    const headers = new HttpHeaders({
      Authorization: authenticationToken,
      responseType: 'json',
      accept: 'application/json'
    });

    return this.http.post(`${environment.apiBaseUrl}${this.generateComponentByAiPath}`, formData, {
      headers,
    });
  }

  /**
  * Update component definition using AI
  * This method is used when a display component already exists and needs to be updated
  * Supports both Angular and Flutter frameworks
  * @param componentDefinitionId - The ID of the component definition to update
  * @param message - The AI prompt message for the update
  * @param componentFramework - The framework type (angular or flutter)
  * @param files - Optional files to attach to the update request
  * @param draftId - Optional existing draft ID to continue. If omitted, starts a new draft
  * @param model - Optional AI model ID override
  * @returns Observable with the update response
  */
  public updateComponentByAI(
    componentDefinitionId: string,
    message: string,
    componentFramework?: 'angular' | 'flutter',
    files?: File[],
    draftId?: string,
    model?: string
  ): Observable<any> {
    const authenticationToken = this.tokenService.getAuthenticationToken();
    const formData = new FormData();

    formData.append('message', message);

    if (componentFramework) {
      formData.append('componentFramework', componentFramework);
    } else {
      formData.append('componentFramework', 'angular');
    }

    if (files?.length) {
      files.forEach(file => {
        formData.append('file', file);
      });
    }

    if (draftId) {
      formData.append('draftId', draftId);
    }

    const modelToUse = model || 'gemini-2.5-pro';
    formData.append('model', modelToUse);

    const headers = new HttpHeaders({
      Authorization: authenticationToken,
      responseType: 'json',
      accept: 'application/json'
    });

    return this.http.post(
      `${environment.apiBaseUrl}/component-definitions/${componentDefinitionId}/update-by-ai`,
      formData,
      { headers }
    );
  }

  /**
   * Get compiled component bundle content (backward compatible wrapper)
   * @param componentDefinitionId - Unique identifier of the component definition
   * @param componentType - Deprecated, kept for backward compatibility only
   * @returns Observable with bundle content as string
   */
  public getCompiledComponentBundleContent(
    componentDefinitionId: string,
    componentType: 'angular' | 'flutter' = 'angular'
  ): Observable<string> {
    return this.getMainBundleContent(componentDefinitionId);
  }

  /**
   * Get component definition source file content
   * Retrieves individual source files (e.g., .dart, .ts files) - not bundle files
   * @param componentDefinitionId - Unique identifier of the component definition
   * @param fileName - Name of the source file to retrieve
   * @returns Observable with file content as string
   */
  public getComponentDefinitionFileContent(componentDefinitionId: string, fileName: string): Observable<string> {
    const url = `${environment.apiBaseUrl}/source-code/component-definition-file-content?filePath=${componentDefinitionId}/${fileName}`;
    const headers = new HttpHeaders({
      Authorization: this.tokenService.getAuthenticationToken(),
      responseType: 'text',
      accept: 'application/json'
    });

    return this.http.get(url, { headers, responseType: 'text' });
  }

  /**
   * Get component definition source directory entries
   * @param componentDefinitionId - Unique identifier of the component definition
   * @returns Observable with directory entries as string
   */
  public getComponentDefinitionSourceDirEntries(componentDefinitionId: string): Observable<string> {
    const url = `${environment.apiBaseUrl}/source-code/component-definition-file-content?filePath=${componentDefinitionId}`;
    const headers = new HttpHeaders({
      Authorization: this.tokenService.getAuthenticationToken(),
      responseType: 'text',
      accept: 'application/json'
    });

    return this.http.get(url, { headers, responseType: 'text' });
  }

  /**
   * Get compiled component status
   * Retrieves the status of the component compilation process
   * @param componentDefinitionId - Unique identifier of the component definition
   * @returns Observable with compilation process status
   */
  public getCompiledComponentStatus(componentDefinitionId: string): Observable<ComponentCompilingProcessStatus> {
    const url = `${environment.apiBaseUrl}/component-compiler/component-compiling-process/${componentDefinitionId}`;
    const headers = new HttpHeaders({
      Authorization: this.tokenService.getAuthenticationToken(),
      responseType: 'text',
      accept: 'application/json'
    });

    return this.http.get<ComponentCompilingProcessStatus>(url, { headers }).pipe(
      catchError(error => {
        if (error.status === 400) {
          console.log('Error fetching component compilation status:', error);
          return of(<ComponentCompilingProcessStatus>{
            id: '',
            componentDefinitionId: componentDefinitionId,
            status: 'not-started',
            startDate: '',
            endDate: ''
          });
        }
        throw error;
      })
    );
  }

  /**
   * Flattens all nested objects within the `children` property recursively
   * @param contentTreeItem - The object with nested `children`
   * @returns A flat array of all container and component objects
   */
  public flattenChildrenOfTreeItem(contentTreeItem: any): any[] {
    const flatList = [];

    function traverse(node) {
      if (!node) {
        return null;
      }

      // Push the current node to the flat list
      flatList.push(lodash.omit(node, 'children'));

      // Recurse into children if present
      if (lodash.isArray(node.children)) {
        lodash.forEach(node.children, traverse);
      }
    }

    lodash.forOwn(contentTreeItem, traverse);
    return flatList;
  }

  /**
   * Updates component definition files
   */
  public updateComponentDefinitionFiles(componentDefinitionId: string, files: any[]): Observable<any> {
    const body = { files };

    return this.http.patch(`${environment.apiBaseUrl}/component-definitions/${componentDefinitionId}/update-files`, body, {
      headers: new HttpHeaders({
        Authorization: this.tokenService.getAuthenticationToken(),
        'Content-Type': 'application/json'
      })
    }).pipe(
      catchError(error => {
        console.error('Error updating component definition files:', error);
        return of(null);
      })
    );
  }

  /**
   * Get branch bundle URL for a component
   * @param componentId - Unique identifier of the component definition
   * @param stepId - Optional step ID to get bundle from specific step. If not provided, returns latest step's bundle
   * @returns Branch bundle URL string
   */
  public getBranchBundleUrl(componentId: string, stepId?: string): string {
    const baseUrl = `${environment.apiBaseUrl}/component-definitions/${componentId}/draft-bundle`;
    return stepId ? `${baseUrl}?stepId=${stepId}` : baseUrl;
  }

  /**
   * Get main bundle URL for a component (approved/active version)
   * @param componentId - Unique identifier of the component definition
   * @param version - Optional version to get bundle from. If not provided, returns active version's bundle
   * @returns Main bundle URL string
   */
  public getMainBundleUrl(componentId: string, version?: string): string {
    const baseUrl = `${environment.apiBaseUrl}/component-definitions/${componentId}/main-bundle`;
    return version ? `${baseUrl}?version=${version}` : baseUrl;
  }

  /**
   * Convert blob to string
   */
  private blobToString(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(blob);
    });
  }

  /**
   * Get branch bundle content for a component
   * @param componentId - Unique identifier of the component definition
   * @param stepId - Optional step ID to get bundle from specific step
   * @returns Observable with bundle content as string
   */
  public getBranchBundleContent(componentId: string, stepId?: string): Observable<string> {
    return this.componentDefinitionControllerService.getDraftBundle$Response({
      componentId,
      stepId
    }).pipe(
      switchMap(response => this.blobToString(response.body as unknown as Blob)),
      catchError(error => {
        console.error('Error fetching branch bundle:', error);
        return of('');
      })
    );
  }

  /**
   * Get main bundle content for a component (approved/active version)
   * @param componentId - Unique identifier of the component definition
   * @param version - Optional version to get bundle from
   * @returns Observable with bundle content as string
   */
  public getMainBundleContent(componentId: string, version?: string): Observable<string> {
    return this.componentDefinitionControllerService.getMainBundle$Response({
      componentId,
      version
    }).pipe(
      switchMap(response => this.blobToString(response.body as unknown as Blob)),
      catchError(error => {
        console.error('Error fetching main bundle:', error);
        return of('');
      })
    );
  }

  /**
   * Get all branches for a component
   * @param componentId - Unique identifier of the component definition
   * @returns Observable with array of branch objects
   */
  public getComponentBranches(componentId: string): Observable<any[]> {
    return this.store.select(selectActiveProjectId).pipe(
      filter((activeProjectId): activeProjectId is string => !!activeProjectId),
      first(),
      switchMap(() => this.componentDefinitionControllerService.getDraftsByComponentId({ componentId }).pipe(
        catchError(error => {
          console.error('Error fetching component branches:', error);
          return of([]);
        })
      ))
    );
  }

  /**
   * Get branch by ID with steps and compilation information
   * @param branchId - Unique identifier of the branch
   * @returns Observable with branch object
   */
  public getBranchById(branchId: string): Observable<any> {
    return this.componentDefinitionControllerService.getDraftById({ draftId: branchId }).pipe(
      catchError(error => {
        console.error('Error fetching branch:', error);
        return of(null);
      })
    );
  }

  /**
   * Get branch steps
   * @param branchId - Unique identifier of the branch
   * @returns Observable with array of branch step objects
   */
  public getBranchSteps(branchId: string): Observable<any[]> {
    return this.componentDefinitionControllerService.getDraftSteps({ draftId: branchId }).pipe(
      catchError(error => {
        console.error('Error fetching branch steps:', error);
        return of([]);
      })
    );
  }

  /**
   * Approve a branch
   * @param branchId - Unique identifier of the branch
   * @param postPlacementTarget - Optional placement target for post-approval
   * @param versionDescription - Optional version description
   * @returns Observable with approved component definition
   */
  public approveBranch(
    branchId: string,
    postPlacementTarget?: { pageId?: string; contentTreeItemId?: string },
    versionDescription?: string
  ): Observable<ComponentDefinition | null> {
    const body: ApproveComponentDraftRequestDtoPartial = {};

    if (postPlacementTarget) {
      body.postPlacementTarget = postPlacementTarget;
    }

    if (versionDescription) {
      body.versionDescription = versionDescription;
    }

    return this.componentDefinitionControllerService.approveDraft({ draftId: branchId, body }).pipe(
      catchError(error => {
        console.error('Error approving branch:', error);
        return of(null);
      })
    );
  }

  /**
   * Reject a branch
   * @param branchId - Unique identifier of the branch
   * @returns Observable that completes when branch is rejected
   */
  public rejectBranch(branchId: string): Observable<void> {
    return this.componentDefinitionControllerService.rejectDraft({ draftId: branchId });
  }

  /**
   * Revert branch to a specific step
   * @param branchId - Unique identifier of the branch
   * @param stepId - Unique identifier of the step to revert to
   * @returns Observable that completes when revert is done
   */
  public revertToStep(branchId: string, stepId: string): Observable<void> {
    return this.componentDefinitionControllerService.revertToStep({ draftId: branchId, stepId }).pipe(
      catchError(error => {
        console.error('Error reverting to step:', error);
        return of(undefined);
      })
    );
  }

  /**
   * Change active version of a component
   * @param componentId - Unique identifier of the component definition
   * @param targetVersion - Version to set as active
   * @returns Observable with updated component definition
   */
  public changeActiveVersion(componentId: string, targetVersion: string): Observable<any> {
    return this.componentDefinitionControllerService.changeActiveVersion({
      componentId,
      body: { targetVersion }
    }).pipe(
      catchError(error => {
        console.error('Error changing active version:', error);
        return of(null);
      })
    );
  }

  /**
   * Set display component for project model
   * @param projectModelId - Unique identifier of the project model
   * @param componentDefinitionId - Unique identifier of the component definition
   * @param componentFramework - Component framework type (angular|flutter)
   * @returns Observable that completes when display component is set
   */
  public setDisplayComponent(
    projectModelId: string,
    componentDefinitionId: string,
    componentFramework: 'angular' | 'flutter' = 'flutter'
  ): Observable<void> {
    const url = `${environment.apiBaseUrl}/project-models/${projectModelId}/set-display-component`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.tokenService.getAuthenticationToken()
    });

    const body = {
      componentDefinitionId,
      componentFramework
    };

    return this.http.post<void>(url, body, { headers }).pipe(
      catchError(error => {
        console.error('Error setting display component:', error);
        throw error;
      })
    );
  }
}
